import { useState, useEffect } from "react";
import { Loader2, Trash2, X, Check, Key, Copy, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "./shared/format";
import { LoadingRow, EmptyState } from "./shared/ui";

// -------- Koppelingen (API keys) Admin --------
export function ApiKeysAdmin() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("read"); // "read" | "questions:write"
  const [freshKey, setFreshKey] = useState(null); // { name, plaintext_key } — one-shot display
  const [revokingId, setRevokingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/apikeys");
      setKeys(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createKey = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Naam mag niet leeg zijn.");
      return;
    }
    setCreating(true);
    try {
      const scopes = newScope === "questions:write" ? ["read", "questions:write"] : ["read"];
      const { data } = await api.post("/admin/apikeys", { name, scopes });
      setFreshKey({ name: data.name, plaintext_key: data.plaintext_key });
      setNewName("");
      setNewScope("read");
      setShowNew(false);
      await load();
      toast.success("Sleutel aangemaakt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (k) => {
    if (!k.active) return;
    if (!window.confirm(`Sleutel "${k.name}" intrekken? Externe tools verliezen direct toegang.`)) return;
    setRevokingId(k.id);
    try {
      await api.delete(`/admin/apikeys/${k.id}`);
      setKeys((prev) =>
        prev.map((x) => (x.id === k.id ? { ...x, active: false, revoked_at: new Date().toISOString() } : x))
      );
      toast.success("Sleutel ingetrokken.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevokingId(null);
    }
  };

  const hardDeleteKey = async (k) => {
    if (k.active) {
      toast.error("Trek de sleutel eerst in.");
      return;
    }
    if (!window.confirm(`Sleutel "${k.name}" definitief verwijderen? Deze actie kan niet ongedaan worden gemaakt.`)) return;
    setRevokingId(k.id);
    try {
      await api.delete(`/admin/apikeys/${k.id}/hard`);
      setKeys((prev) => prev.filter((x) => x.id !== k.id));
      toast.success("Sleutel verwijderd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevokingId(null);
    }
  };

  const copyPlaintext = async () => {
    if (!freshKey?.plaintext_key) return;
    try {
      await navigator.clipboard.writeText(freshKey.plaintext_key);
      setCopied(true);
      toast.success("Gekopieerd.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopiëren mislukt — selecteer handmatig.");
    }
  };

  const curlExample = `curl -H "X-TW1-Key: <JOUW_SLEUTEL>" \\
  "${baseUrl}/api/v1/export/recordings?limit=50"`;

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(curlExample);
      toast.success("Voorbeeld gekopieerd.");
    } catch {
      toast.error("Kopiëren mislukt.");
    }
  };

  return (
    <div data-testid="admin-koppelingen-panel">
      {/* Header + create button */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            API-SLEUTELS
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-sm text-tw-yellow" data-testid="admin-apikeys-count">
            {keys.length}
          </div>
          <Button
            onClick={() => setShowNew((v) => !v)}
            data-testid="admin-apikeys-toggle-new"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3"
          >
            <Key size={12} className="mr-1" strokeWidth={3} /> Nieuwe sleutel
          </Button>
        </div>
      </div>
      <p className="text-xs text-tw-muted mb-5 max-w-2xl leading-relaxed">
        Geef externe TW1-tools zoals Vault leestoegang tot de kluis.
      </p>

      {/* One-shot new-key banner */}
      {freshKey && (
        <div
          className="bg-tw-yellow/10 border-l-4 border-tw-yellow border-y border-r border-tw-yellow p-4 mb-5 tw-fadeup"
          data-testid="admin-apikeys-new-plaintext"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow mb-1 uppercase">
                Nieuwe sleutel · {freshKey.name}
              </div>
              <div className="text-sm text-white leading-snug">
                <strong>Kopieer nu — deze sleutel zie je maar één keer.</strong>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFreshKey(null)}
              className="text-tw-muted hover:text-white shrink-0 p-1"
              aria-label="Sluit"
              data-testid="admin-apikeys-close-plaintext"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
          <div className="flex items-stretch gap-2">
            <code
              data-testid="admin-apikeys-plaintext-value"
              className="flex-1 bg-tw-black border border-tw text-tw-yellow font-mono text-xs px-3 py-2.5 break-all leading-relaxed select-all"
            >
              {freshKey.plaintext_key}
            </code>
            <button
              type="button"
              onClick={copyPlaintext}
              data-testid="admin-apikeys-copy-plaintext"
              className="tw-press shrink-0 h-auto px-3 bg-tw-yellow text-black hover:bg-[#FFDE33] uppercase tracking-[0.2em] text-[10px] font-bold flex items-center gap-1.5"
            >
              {copied ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={2.8} />}
              {copied ? "Gekopieerd" : "Kopieer"}
            </button>
          </div>
        </div>
      )}

      {/* New-key form */}
      {showNew && (
        <div
          className="bg-tw-surface border border-tw-yellow p-4 mb-5 space-y-3 tw-fadeup"
          data-testid="admin-apikeys-new-form"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow">
            NAAM VAN DE INTEGRATIE
          </div>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createKey(); }}
            placeholder="Bv. Vault, Zapier, Interne dashboard…"
            data-testid="admin-apikeys-new-name"
            className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
          />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow pt-1">
            RECHTEN
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="admin-apikeys-scope-choice">
            {[
              { value: "read", label: "Alleen lezen", hint: "Kan de kluis uitlezen." },
              { value: "questions:write", label: "Lezen + vragen beheren", hint: "Mag ook kennisvragen toevoegen en aanpassen." },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setNewScope(opt.value)}
                data-testid={`admin-apikeys-scope-${opt.value === "questions:write" ? "write" : "read"}`}
                aria-pressed={newScope === opt.value}
                className={`tw-press text-left p-3 border transition-colors ${
                  newScope === opt.value
                    ? "border-tw-yellow bg-tw-yellow/10"
                    : "border-tw bg-tw-black hover:border-tw-yellow/60"
                }`}
              >
                <div className={`text-sm font-medium ${newScope === opt.value ? "text-tw-yellow" : "text-white"}`}>
                  {opt.label}
                </div>
                <div className="mt-0.5 font-mono text-[10px] text-tw-muted leading-relaxed">{opt.hint}</div>
              </button>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => { setShowNew(false); setNewName(""); }}
              variant="outline"
              className="h-9 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px]"
            >
              Annuleer
            </Button>
            <Button
              onClick={createKey}
              disabled={creating || !newName.trim()}
              data-testid="admin-apikeys-new-save"
              className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50"
            >
              {creating ? <Loader2 className="animate-spin" size={14} /> : "Aanmaken"}
            </Button>
          </div>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <LoadingRow />
      ) : keys.length === 0 ? (
        <EmptyState label="Nog geen sleutels" hint="Maak er één aan om Vault of andere tools te koppelen." testId="admin-apikeys-empty" />
      ) : (
        <div className="space-y-2" data-testid="admin-apikeys-list">
          {keys.map((k) => (
            <article
              key={k.id}
              data-testid={`admin-apikey-${k.id}`}
              className={`tw-fadeup bg-tw-surface border p-4 transition-colors duration-150 ${
                k.active ? "border-tw hover:border-tw-yellow" : "border-tw opacity-60"
              }`}
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{k.name}</span>
                    {k.active ? (
                      <span className="inline-flex items-center gap-1 border border-[#32D74B] text-[#32D74B] px-1.5 py-0.5 font-mono text-[9px] tracking-[0.3em] uppercase">
                        Actief
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border border-tw-red text-tw-red px-1.5 py-0.5 font-mono text-[9px] tracking-[0.3em] uppercase">
                        Ingetrokken
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 font-mono text-[11px] text-tw-muted">
                    <span data-testid={`admin-apikey-prefix-${k.id}`}>{k.prefix}…</span>
                  </div>
                  <div className="mt-2 font-mono text-[10px] text-tw-muted tracking-wide">
                    Aangemaakt: {formatDate(k.created_at)}
                    {" · "}
                    Laatst gebruikt: {k.last_used_at ? formatDate(k.last_used_at) : "nog niet"}
                    {k.revoked_at && ` · Ingetrokken: ${formatDate(k.revoked_at)}`}
                  </div>
                  <div className="mt-2">
                    <span
                      data-testid={`admin-apikey-scope-${k.id}`}
                      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase ${
                        (k.scopes || []).includes("questions:write")
                          ? "border-tw-yellow text-tw-yellow"
                          : "border-tw text-tw-muted"
                      }`}
                    >
                      {(k.scopes || []).includes("questions:write") ? "Lezen + vragen" : "Alleen lezen"}
                    </span>
                  </div>
                </div>
                {k.active && (
                  <button
                    type="button"
                    onClick={() => revokeKey(k)}
                    disabled={revokingId === k.id}
                    data-testid={`admin-apikey-revoke-${k.id}`}
                    title="Intrekken"
                    aria-label="Intrekken"
                    className="tw-press shrink-0 h-9 px-3 border border-tw text-tw-muted hover:border-tw-red hover:text-tw-red uppercase tracking-[0.2em] text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {revokingId === k.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Ban size={12} strokeWidth={2.8} />
                    )}
                    Intrekken
                  </button>
                )}
                {!k.active && (
                  <button
                    type="button"
                    onClick={() => hardDeleteKey(k)}
                    disabled={revokingId === k.id}
                    data-testid={`admin-apikey-hard-delete-${k.id}`}
                    title="Definitief verwijderen"
                    aria-label="Definitief verwijderen"
                    className="tw-press shrink-0 h-9 px-3 border border-tw text-tw-muted hover:border-tw-red hover:text-tw-red uppercase tracking-[0.2em] text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {revokingId === k.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} strokeWidth={2.8} />
                    )}
                    Verwijder
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Usage / curl example */}
      <div className="mt-8 bg-tw-surface border border-tw p-4 sm:p-5" data-testid="admin-apikeys-usage">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            GEBRUIK
          </div>
          <button
            type="button"
            onClick={copyCurl}
            data-testid="admin-apikeys-copy-curl"
            className="tw-press flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-tw-muted hover:text-tw-yellow"
          >
            <Copy size={11} strokeWidth={2.8} /> Kopieer voorbeeld
          </button>
        </div>
        <p className="text-xs text-tw-muted mb-3 leading-relaxed">
          Stuur je sleutel mee in de <span className="text-white">X-TW1-Key</span> header. Base URL:
          {" "}<span className="text-white font-mono">{baseUrl || "(niet ingesteld)"}</span>
        </p>
        <pre
          data-testid="admin-apikeys-curl-example"
          className="bg-tw-black border border-tw text-tw-yellow font-mono text-[11px] p-3 overflow-x-auto whitespace-pre leading-relaxed"
        >
{curlExample}
        </pre>
        <div className="mt-3 font-mono text-[10px] text-tw-muted leading-relaxed">
          <div>Endpoints:</div>
          <div className="mt-1"><span className="text-white">GET /api/v1/export/recordings</span> — filters: since, category, functie, transform_type, limit, offset</div>
          <div><span className="text-white">GET /api/v1/export/questions</span> — alle vragen (ook inactieve)</div>
          <div><span className="text-white">GET /api/v1/export/categories</span> — categorieën</div>
          <div><span className="text-white">GET /api/v1/export/meta</span> — totalen en aantallen per collectie</div>
          <div className="mt-1"><span className="text-white">POST /api/v1/questions</span> · <span className="text-white">PATCH /api/v1/questions/&#123;id&#125;</span> — alleen met recht "vragen beheren"</div>
        </div>
      </div>
    </div>
  );
}
